import {Component, EventEmitter, Input, NgZone, OnInit, Output, Renderer2} from '@angular/core';
import {UserService} from '../../../services/user.service';
import {CommunicationService} from '../../../services/communication.service';
import {Router} from '@angular/router';
import {LoginResult, LoginService} from '../../../services/login.service';
import {TranslateService} from '@ngx-translate/core';
import {SocialType} from '../../../classes/enums';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import { CustomValidators } from '../../../classes/custom-validators';
import { emailRegex } from '../../../classes/leads';
import { DomManipulationService } from '../../../services/dom-manipulation/dom-manipulation.service';
import { RECAPTCHA_SCRIPT } from '../../../classes/dynamicScripts';
import { PreRenderService } from '../../../services/pre-render.service';

declare var grecaptcha: ReCaptchaV2.ReCaptcha;

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: [
    './sign-in.component.scss',
    './sign-in.component.desktop.scss'
  ]
})
export class SignInComponent implements OnInit {
  @Output() showForgotPassword: EventEmitter<{show: boolean, email: string}> = new EventEmitter();
  @Output() continueWithSocialLogin: EventEmitter<SocialType> = new EventEmitter<SocialType>();
  @Input() email: string;
  error: string;
  accounts = [];
  existingAccounts: string = '';
  showPasswordText: boolean = false;
  showConfirmPasswordText: boolean = false;
  loginAttempt: SocialType;
  buttonMessage: string = '';
  loginMode: boolean = true;
  private returnToUrl: string;

  loginForm: FormGroup;

  recaptchaId: number;
  isLoading: boolean = false;

  hasFacebook: boolean = false;
  hasGoogle: boolean = false;
  hasEmail: boolean = false;

  constructor(private userService: UserService,
              private communicationService: CommunicationService,
              private domManipulationService: DomManipulationService,
              private _loginService: LoginService,
              private router: Router,
              private _translateService: TranslateService,
              private zone: NgZone,
              private renderer: Renderer2,
              private prerenderService: PreRenderService
  ) {
    // Store the url we came from in order to send the user back after a successful login.
    // remove the popup data from the url
    this.returnToUrl = this.router.routerState.snapshot.url.replace(/\(.*\)/, '');

    this.loginForm = new FormGroup({
      email: new FormControl('', {validators: [Validators.required, Validators.pattern(emailRegex)], updateOn: 'change'}),
      password: new FormGroup({
        password: new FormControl('', {validators: [Validators.required, Validators.minLength(6)], updateOn: 'change'}),
        confirmPassword: new FormControl('', {validators: [Validators.minLength(6)], updateOn: 'change'})
      }),
      termsAndCond: new FormControl('', [Validators.required]),
      rememberMe: new FormControl(true)
    });
  }

  ngOnInit() {
    if (this.prerenderService.isBrowserMode) {
      this.renderer.addClass(document.querySelector('html'), 'no-scroll');
    }
    if (this.email) {
      this.loginForm.controls['email'].setValue(this.email);
    }
  }

  sendGrecaptcha() {
    this.domManipulationService.loadScript(RECAPTCHA_SCRIPT).subscribe(() => {
      grecaptcha.execute(this.recaptchaId);
    });
  }

  getOnSubmit(): (recaptchaKey: string) => void {
    return this.onSubmit.bind(this);
  }

  close() {
    this.renderer.removeClass(document.querySelector('html'), 'no-scroll');
    this.communicationService.showSignIn.next(false);
  }

  setLoginMode(mode) {
    if (mode) this.loginForm.get('password').clearValidators();
    else this.loginForm.get('password').setValidators(CustomValidators.passwordMatcher);
    this.loginMode = mode;
  }

  /**
   * Called from onSubmit after the user clicks the login/register button, so we know they are not doing a social login.
   * @param isNewlyRegistered
   */
  login(isNewlyRegistered: boolean = false) {
    this.setLoginAttempt(SocialType.NotSocial);
    this.userService.checkUserAccounts(this.userEmail.value).then(response => {
      if (response.filter(r => r === SocialType.NotSocial)) {
        this.continueWithLogin(isNewlyRegistered);
        return;
      }
      this.showUserMessage(response);
    }).catch(error => {
      if (error.status === 404) {
        this.continueWithLogin(isNewlyRegistered);
      }
    });
  }

  /**
   * We've detected the email they are trying to log in with has already been used to register a social account with.
   * @param accounts
   */
  showUserMessage(accounts: Array<SocialType>) {
    this.accounts = accounts;
    this.hasFacebook = accounts.indexOf(SocialType.Facebook) > -1;
    this.hasGoogle = accounts.indexOf(SocialType.Google) > -1;
    this.hasEmail = accounts.indexOf(SocialType.NotSocial) > -1;

    this.zone.run(() => {
      this.error = 'sign-in.email-exists';
      this.existingAccounts = '';
      for (let i = 0; i < accounts.length; i++) {
        this.existingAccounts += this.getAccountName(accounts[i]) + ' ';
        if (i < accounts.length - 1) {
          this.existingAccounts += this._translateService.instant('sign-in.and') + ' '
        }
      }
    });
  }

  setLoginAttempt(loginAttempt: SocialType) {
    this.loginAttempt = loginAttempt;
    switch (this.loginAttempt) {
      case SocialType.NotSocial:
        this.buttonMessage = 'Credentials';
        break;
      case SocialType.Facebook:
        this.buttonMessage = 'Facebook';
        break;
      case SocialType.Google:
        this.buttonMessage = 'Google';
        break;
    }
  }

  /**
   * This will either switch the login attempt to registration, or if we are doing a social login, we have confirmed with the user
   * that they want to continue (they already have other another account registered with us)
   */
  continueLogin() {
    switch (this.loginAttempt) {
      case SocialType.NotSocial:
        this.switchToRegistration();
        break;
      case SocialType.Facebook:
      case SocialType.Google:
        this.continueWithSocialLogin.next(this.loginAttempt);
        break;
    }
  }

  /**
   * This is called after we have confirmed they have a non-social account or do not yet have an account.
   * @param isNewlyRegistered
   */
  private async continueWithLogin(isNewlyRegistered: boolean) {
    this.isLoading = true;
    this._loginService.login(this.userEmail.value, this.password.value, this.rememberMe.value).subscribe(async (result) => {
      if (result === LoginResult.LoggedIn) {
        // This should take them to the page they were on before they tried to login.
        this.router.navigateByUrl(this.returnToUrl);
        this.communicationService.navigation.toggleNavs.next({left: false, right: false});
        // Remove anything that might have taken away scrolling
        this.renderer.removeClass(document.querySelector('html'), 'no-scroll');

        this.close();
      } else if (result === LoginResult.InvalidCredentails) {
        this.error = 'registration.invalid-username-password';
      } else if (result === LoginResult.NewUserRequiresPasswordChange) {
        this.error = 'registration.migrated-user';
      } else if (result === LoginResult.UserNotFound) {
        this.setLoginMode(false);
      } else {
        this.error = 'unexpected-error';
      }
      this.isLoading = false;
    });
  }

  /**
   * Called when the user clicks the login/register button.
   */
  onSubmit(recaptchaKey?: string) {
    if (this.loginMode) {
      this.login(false);
    } else {
      const value = this.loginForm.value;
      value.password = this.password.value;
      value.confirmPassword = this.confirmPassword.value;
      this.userService.registerUser(value, recaptchaKey).then(data => {
        // If they successfully register, automatically log them in.
        this.login(true);
      }).catch(error => {
        this.error = error.error ? error.error.message : JSON.parse(error['_body']).message;
      });
    }
  }

  getAccountName(account: SocialType) {
    if (account === SocialType.Facebook) return 'Facebook';
    if (account === SocialType.Google) return 'Google';
    if (account === SocialType.NotSocial) return 'Email';
  }

  switchToEmail() {
    this.error = '';
    this.accounts = [];
  }

  switchToRegistration() {
    this.error = '';
    this.loginMode = false;
    this.accounts = [];
  }

  get password() {
    return this.loginForm.get('password').get('password');
  }

  get confirmPassword() {
    return this.loginForm.get('password').get('confirmPassword');
  }

  get userEmail() {
    return this.loginForm.get('email');
  }

  get passwordFormGroup() {
    return this.loginForm.get('password');
  }

  get rememberMe() {
    return this.loginForm.get('rememberMe');
  }

  // enums
  SocialType = SocialType;
}

export class LoginOrRegister {
  public email: string;
  public password: string;
  public confirmPassword: string;
  public termsAndCond: boolean;
  public rememberMe: boolean = true;
}
